














































































































import OtCheckboxGroup, { CheckboxGroupItem } from '@/components/global/checkbox-group/ot-checkbox-group.vue';
import OtButton, { OtBtnSize, OtBtnStyle, OtBtnType } from '@/components/global/ot-button.vue';
import OtTag from '@/components/global/ot-tag.vue';
import OtTableHeader from '@/components/global/table/ot-table-header.vue';
import { IColumnData, IExtendedColumnData } from '@/components/global/table/ot-table-models';
import { getPaginatedBodyData, handleHeaderClick, sortTableData } from '@/components/global/table/ot-table-utils';
import OtTable from '@/components/global/table/ot-table.vue';
import OtApi, { executeApi, ExecuteApiValue, IApiErrorOverride } from '@/services/api.service';
import { Component, Prop, Ref, Vue } from 'vue-property-decorator';

import { OtUserStatus } from '@/types/status-enums';
import { RawLocation } from 'vue-router/types/router';
import { AccessType } from '../../../models/shared-models';

import { ROUTE_CONTRACT_DETAILS } from '@/areas/projects/contracts/contract-routes';
import OtUserStatusTags from '@/components/global/ot-user-status-tags.vue';
import { parsedApiOrgUserStatusEnums } from '@/models/user-models';
import { PostSetContractUserStatusUserStatusModel } from '@/services/generated/api';
import { ProjectUserContract } from './project-user-contract-models';
import OtInviteUserDialog, { InviteUserDialogParams } from '@/areas/invitations/ot-invite-user-dialog.vue';
import { OtUserAction } from '@/types/action-enums';
import { ProjectUserDetails } from './project-user-models';
import { ContractUser } from '../contracts/contract-user-models';

interface IMenuOption {
  key: string;
  label: string;
  onClick: (contract: ProjectUserContract) => void;
}

@Component({
  components: {
    OtTable,
    OtTableHeader,
    OtCheckboxGroup,
    OtButton,
    OtTag,
    OtUserStatusTags,
    OtInviteUserDialog,
  },
})
export default class OtProjectUserContractsTable extends Vue {
  // * PROPS
  @Prop({ default: () => [] }) private projectUserContracts!: ProjectUserContract[];
  @Prop() private projectUser!: ProjectUserDetails;

  // * REFS

  // * DATA
  private api = new OtApi();
  private page = 1;
  private searchText = '';
  private rowLoadingStates: { [key: string]: boolean } = {};
  private buttonSize = OtBtnSize.Tiny;
  private buttonStyle = OtBtnStyle.Outline;
  private buttonType = OtBtnType.Icon;

  private tableColumns: IExtendedColumnData<ProjectUserContract>[] = [
    {
      index: 0,
      label: 'Reference',
      key: 'reference',
      isActive: true,
      ascending: true,
      sortable: true,
      sortFunction: ProjectUserContract.compareByReference,
    },
    {
      index: 1,
      label: 'Contracts',
      key: 'name',
      isActive: false,
      ascending: false,
      sortable: true,
      sortFunction: ProjectUserContract.compareByName,
    },
    {
      index: 2,
      label: 'Contract Role',
      key: 'title',
      isActive: false,
      ascending: false,
      sortable: true,
      sortFunction: ProjectUserContract.compareByContractRole,
    },
    {
      index: 3,
      label: 'Org.',
      key: 'org',
      isActive: false,
      ascending: false,
      sortable: false,
    },
    {
      index: 4,
      label: 'Project',
      key: 'project',
      isActive: false,
      ascending: false,
      sortable: false,
    },
    {
      index: 5,
      label: 'Contracts',
      key: 'contract',
      isActive: false,
      ascending: false,
      sortable: false,
    },
    {
      index: 6,
      label: 'Actions',
      key: 'actions',
      isActive: false,
      ascending: false,
      sortable: false,
    },
  ];

  private activeColumn = this.tableColumns[0];

  private openUserStatusFilters: CheckboxGroupItem<OtUserStatus>[] = [
    new CheckboxGroupItem('Active', OtUserStatus.Active),
    new CheckboxGroupItem('Inactive', OtUserStatus.Inactive),
    new CheckboxGroupItem('Invited', OtUserStatus.Invited),
    new CheckboxGroupItem('Rejected Invite', OtUserStatus.RejectedInvitation),
  ];

  private selectedUserStatusFilters: CheckboxGroupItem<OtUserStatus>[] = [
    new CheckboxGroupItem('Active', OtUserStatus.Active),
    new CheckboxGroupItem('Invited', OtUserStatus.Invited),
  ];

  private typeOfAccessFilters: CheckboxGroupItem<AccessType>[] = [
    new CheckboxGroupItem('Projects', AccessType.Projects),
    new CheckboxGroupItem('Contracts', AccessType.Contracts),
    new CheckboxGroupItem('Organisation', AccessType.Organisations),
  ];

  private selectedTypeOfAccessFilters: CheckboxGroupItem<AccessType>[] = [];

  // * COMPUTED

  private get sortedBodyData() {
    const sortedProjectUsers = sortTableData(this.tableColumns, this.activeColumn, this.projectUserContracts);

    return sortedProjectUsers;
  }

  private get filteredBodyData() {
    const parsedStatuses = this.selectedUserStatusFilters.map(s => s.value);
    const parsedAccessTypes = this.selectedTypeOfAccessFilters.map(s => s.value);
    return this.sortedBodyData.filter(row => {
      return (
        ProjectUserContract.filterByUserStatus(parsedStatuses, row) &&
        ProjectUserContract.filterByTypeOfAccess(parsedAccessTypes, row) &&
        ProjectUserContract.filterByName(this.searchText, row)
      );
    });
  }

  private get paginatedBodyData() {
    return getPaginatedBodyData(this.filteredBodyData);
  }

  private get currentBodyData() {
    return this.paginatedBodyData[this.page - 1];
  }

  private get pageCount() {
    let currentMaxPage = this.paginatedBodyData.length;
    if (this.page > currentMaxPage && currentMaxPage >= 1) {
      currentMaxPage = this.paginatedBodyData.length - 1;
      this.page = currentMaxPage;
    }
    return currentMaxPage;
  }

  // * WATCHERS

  // * METHODS

  private updateCurrentPage(value: number) {
    this.page = value;
  }

  private headerClick(header: IColumnData) {
    const handledHeaders = handleHeaderClick(this.tableColumns, this.activeColumn, header);
    this.tableColumns = handledHeaders.tableColumns;
    this.activeColumn = handledHeaders.activeColumn;
  }

  private getProjectUserContractDetailsRoute(contract: ProjectUserContract): RawLocation {
    return {
      name: ROUTE_CONTRACT_DETAILS,
      params: { projectGid: contract.projectGid, contractGid: contract.gid },
    };
  }

  private handleDeactivateForContract = (contract: ProjectUserContract): IMenuOption => {
    return {
      key: contract.gid,
      label: 'Deactivate for Contract',
      onClick: () => {
        this.setProjectUserToDeactivateForContract(contract);
      },
    };
  };

  private handleReinviteUser = (contract: ProjectUserContract): IMenuOption => {
    return {
      key: contract.gid,
      label: 'Reinvite User',
      onClick: () => {
        this.reInviteUser(contract);
      },
    };
  };

  private async setProjectUserToDeactivateForContract(contract: ProjectUserContract) {
    this.rowLoadingStates[contract.gid] = true;

    const errorOverride: IApiErrorOverride = {
      status: 422,
      // NOTE which id are be to give to the warning ? should it be a n array of contract ids ?
      message: `Unable to deactivate user ${contract.projectUser?.gid} for contract, ${ExecuteApiValue.ValidationError}`,
    };
    const body: PostSetContractUserStatusUserStatusModel[] = [
      new PostSetContractUserStatusUserStatusModel({
        gid: contract.contractUser?.gid || '',
        status: parsedApiOrgUserStatusEnums[OtUserStatus.Inactive],
      }),
    ];

    if (body.length === 0) {
      this.rowLoadingStates[contract.gid] = false;
      return;
    }
    const postResponse = await executeApi(
      () => this.api.contractUser().postSetContractUserStatus(body),
      `Deactivating user in contract`,
      errorOverride,
    );

    if (postResponse.success) {
      this.$emit('reloadProjectUserContracts', () => {
        this.rowLoadingStates[contract.gid] = false;
      });
    } else {
      this.rowLoadingStates[contract.gid] = false;
    }
  }

  @Ref('inviteUserDialogRef')
  private readonly inviteUserDialogRef!: OtInviteUserDialog;

  private async reInviteUser(contract: ProjectUserContract) {
    this.rowLoadingStates[contract.gid] = true;

    const reInviteToOrg = contract.organisationUser?.availableActions.includes(OtUserAction.Reinvite)
      ? contract.organisationUser
      : null;
    const reInviteToProjects = contract.projectUser?.availableActions.includes(OtUserAction.Reinvite)
      ? [contract.projectUser]
      : [];
    const reinviteToContracts = contract.contractUser?.availableActions.includes(OtUserAction.Reinvite)
      ? [contract.contractUser]
      : [];

    const params = new InviteUserDialogParams({
      isReinvite: true,
      organisationGid: contract.organisationGid,
      inviteToOrganisation: Boolean(reInviteToOrg),
      invitedUserGid: this.projectUser.gid, // this is the user gid, not the project user gid
      userContracts: reinviteToContracts.map(x => x.gid),
      userProjects: reInviteToProjects.map(x => x.gid),
    });

    const dialogResult = await this.inviteUserDialogRef.open(params);
    if (dialogResult) {
      // Do nothing at this stage as per spec. But may need a snack bar later on
    }

    this.rowLoadingStates[contract.gid] = false;
  }

  private getProjectUserContractOptionMenuItems(contract: ProjectUserContract): IMenuOption[] {
    const results: IMenuOption[] = [];

    const canDeactivateForContract = contract.contractUser?.availableActions?.includes(OtUserAction.Deactivate);

    if (canDeactivateForContract) {
      results.push(this.handleDeactivateForContract(contract));
    }

    const reinviteAction =
      contract.organisationUser?.availableActions?.includes(OtUserAction.Reinvite) ||
      contract.projectUser?.availableActions?.includes(OtUserAction.Reinvite) ||
      contract.contractUser?.availableActions?.includes(OtUserAction.Reinvite);
    if (reinviteAction) {
      results.push(this.handleReinviteUser(contract));
    }

    return results;
  }

  private setRowLoadingStates() {
    this.rowLoadingStates = this.projectUserContracts.reduce((a, x) => ({ ...a, [x.gid]: false }), {});
  }

  // * LIFECYCLE
  private created() {
    this.setRowLoadingStates();
  }
}
